import {
  extractS3KeyFromUrl,
  getApiBaseUrl,
  getSecureCookie,
  processJsonResponse,
} from "@ippon/shared-utils";

const PDF_MAGIC_NUMBER = [0x25, 0x50, 0x44, 0x46];
const ERROR_TEXT_PREVIEW_SIZE = 100;
const MAX_CACHE_SIZE = 50;

// S3キー基準のメモリキャッシュ（同一PDFの再取得を防止）
const pdfCache = new Map<string, Uint8Array>();
// インフライト重複排除（同時リクエストの重複を防止）
const inFlightRequests = new Map<string, Promise<Uint8Array>>();

const isPdfMagicNumber = (bytes: Uint8Array): boolean => {
  if (bytes.length < 4) return false;
  return (
    bytes[0] === PDF_MAGIC_NUMBER[0] &&
    bytes[1] === PDF_MAGIC_NUMBER[1] &&
    bytes[2] === PDF_MAGIC_NUMBER[2] &&
    bytes[3] === PDF_MAGIC_NUMBER[3]
  );
};

const clonePdfBytes = (bytes: Uint8Array): Uint8Array => new Uint8Array(bytes);

const parseErrorResponse = (uint8Array: Uint8Array): never => {
  const textDecoder = new TextDecoder();
  const firstBytes = Array.from(uint8Array.slice(0, 4));
  const errorText = textDecoder.decode(
    uint8Array.slice(0, ERROR_TEXT_PREVIEW_SIZE)
  );
  console.error(
    "Response is not PDF. First bytes:",
    firstBytes,
    "First 100 chars:",
    errorText
  );
  throw new Error("Response is not a PDF");
};

export const fetchPdfFromS3 = async (pdfUrl: string): Promise<Uint8Array> => {
  const s3Key = extractS3KeyFromUrl(pdfUrl);
  if (!s3Key) {
    throw new Error("Failed to extract S3 key from URL");
  }

  // キャッシュヒットチェック（pdfjs-distがArrayBufferをdetachするためコピーを返す）
  const cached = pdfCache.get(s3Key);
  if (cached) {
    // アクセスされた項目を最後に移動してLRUを実装
    pdfCache.delete(s3Key);
    pdfCache.set(s3Key, cached);
    return clonePdfBytes(cached);
  }

  // インフライト重複排除（コピーを返す）
  const inflight = inFlightRequests.get(s3Key);
  if (inflight) {
    return inflight.then((data) => clonePdfBytes(data));
  }

  const fetchPromise = (async () => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      const token = getSecureCookie("token");

      const response = await fetch(`${apiBaseUrl}/api/v1/download/file`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ key: s3Key }),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch PDF: ${response.status} ${response.statusText}`
        );
      }

      const arrayBuffer = await response.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);

      if (uint8Array.length === 0) {
        throw new Error("Empty response");
      }

      let result: Uint8Array;

      if (isPdfMagicNumber(uint8Array)) {
        result = uint8Array;
      } else {
        const bufferArray = processJsonResponse(uint8Array, {
          throwOnNonBufferJson: true,
        });
        if (bufferArray) {
          result = bufferArray;
        } else {
          return parseErrorResponse(uint8Array);
        }
      }

      const cacheEntry = clonePdfBytes(result);

      // キャッシュに保存（サイズ上限超過時は最古を削除）
      if (pdfCache.size >= MAX_CACHE_SIZE) {
        const firstKey = pdfCache.keys().next().value;
        if (firstKey) pdfCache.delete(firstKey);
      }
      pdfCache.set(s3Key, cacheEntry);

      return clonePdfBytes(cacheEntry);
    } finally {
      inFlightRequests.delete(s3Key);
    }
  })();

  inFlightRequests.set(s3Key, fetchPromise);
  return fetchPromise;
};

export const clearPdfCache = () => {
  pdfCache.clear();
  inFlightRequests.clear();
};

export const preparePdfSource = async (
  pdfUrl: string
): Promise<string | Uint8Array> => {
  if (pdfUrl.startsWith("blob:")) {
    return pdfUrl;
  }

  if (pdfUrl.startsWith("data:application/pdf;base64,")) {
    const response = await fetch(pdfUrl);
    const arrayBuffer = await response.arrayBuffer();
    return new Uint8Array(arrayBuffer);
  }

  if (
    pdfUrl.startsWith("https://") &&
    pdfUrl.includes("s3") &&
    pdfUrl.includes("amazonaws.com")
  ) {
    // API経由でS3からPDFを取得（CORS回避のため）
    return await fetchPdfFromS3(pdfUrl);
  }

  return pdfUrl;
};
